// Shared reel timing: group a flat shot list (per-frame) into shot groups
// (a multi-keyframe move = one group), and time each group to the LONGER of its
// authored duration or its voice-over audio — so VO lines never get clipped.
// Also builds the EDL (edit-model) the engine + editor consume.

import { getFrameImageUrl, getReelAudioUrl } from "@/services/shotService";
import { getReelAssetUrl } from "@/services/editService";

const PAD_SECONDS = 0.5; // breathing room after a spoken line

export function buildGroups(shots, fps) {
  const groups = [];
  let cur = null;
  (shots || []).forEach((s) => {
    const gk = s.group_key || s.frame_id;
    if (!cur || cur.group_key !== gk) {
      cur = { group_key: gk, frames: [], vo_text: null, vo_speaker: null, has_audio: false, audio_seconds: null };
      groups.push(cur);
    }
    cur.frames.push(s);
    if (s.has_audio) { cur.has_audio = true; cur.audio_seconds = s.audio_seconds; }
    if (s.vo_text && !cur.vo_text) { cur.vo_text = s.vo_text; cur.vo_speaker = s.vo_speaker; }
  });

  let acc = 0;
  groups.forEach((g) => {
    const authored = g.frames.reduce(
      (a, f) => a + Math.max(1, Math.round((f.duration_seconds || 2.5) * fps)), 0);
    const audioF = g.has_audio && g.audio_seconds ? Math.round((g.audio_seconds + PAD_SECONDS) * fps) : 0;
    g.durationInFrames = Math.max(authored, audioF, 1);
    g.startFrame = acc;
    g.lead = g.frames[0];
    acc += g.durationInFrames;
  });
  return { groups, totalFrames: Math.max(1, acc) };
}

// Build the EDL (edit-model) from the /reel data: a video track (one clip per
// keyframe), a VO audio track, and a subtitle track. This is the seed our own
// engine + editor consume (and, later, what persists/edits).
export function buildEdlFromReel(reel) {
  const fps = reel?.fps || 30;
  const { groups, totalFrames } = buildGroups(reel?.shots || [], fps);
  const video = { id: "video", kind: "video", gain: 1, clips: [] };
  const vo = { id: "vo", kind: "vo", gain: 1, clips: [] };
  const subtitles = [];
  groups.forEach((g) => {
    const start = g.startFrame / fps;
    const dur = g.durationInFrames / fps;
    const n = g.frames.length;
    // A finished VFX transform or Veo animation plays ONE moving clip for the
    // whole group (VFX wins — it's applied on top; poster = the original still);
    // otherwise the per-keyframe stills.
    const vfxFrame = g.frames.find((f) => f.vfx_result?.status === "done" && f.vfx_result.asset_key);
    const animFrame = g.frames.find((f) => f.animation?.status === "done" && f.animation.asset_key);
    const movingKey = vfxFrame?.vfx_result?.asset_key || animFrame?.animation?.asset_key;
    if (movingKey) {
      video.clips.push({
        id: `v-${g.lead.frame_id}`, frame_id: g.lead.frame_id,
        asset_key: movingKey, mediaType: "video",
        src: getReelAssetUrl(movingKey), poster: getFrameImageUrl(g.lead.frame_id),
        start, duration: dur, in: 0, group_key: g.group_key,
        meta: { shot: g.lead.authored_shot_number ?? g.lead.frame_number, timecode: g.lead.timecode, shot_type: g.lead.shot_type },
      });
    } else g.frames.forEach((kf, i) => {
      video.clips.push({
        id: `v-${kf.frame_id}`,
        frame_id: kf.frame_id,
        src: getFrameImageUrl(kf.frame_id),
        start: start + (i * dur) / n,
        duration: dur / n,
        group_key: g.group_key,
        meta: { shot: kf.authored_shot_number ?? kf.frame_number, timecode: kf.timecode, shot_type: kf.shot_type },
      });
    });
    if (g.has_audio) {
      vo.clips.push({ id: `a-${g.group_key}`, group_key: g.group_key, src: getReelAudioUrl(g.group_key), start, duration: dur, gain: 1 });
    }
    if (g.vo_text) subtitles.push({ group_key: g.group_key, start, duration: dur, text: g.vo_text, speaker: g.vo_speaker });
  });
  return { fps, width: 1920, height: 1080, totalSec: totalFrames / fps,
           order: groups.map((g) => g.group_key), tracks: [video, vo], subtitles, groups };
}

// Derive the timeline's video row groups FROM the EDL video track (single source
// of truth → always in sync with the canvas + any reordering). Each group = a run
// of consecutive clips sharing a group_key.
export function groupsFromEdl(edl, fps) {
  const clips = (edl?.tracks || []).find((t) => t.kind === "video")?.clips || [];
  const voTrack = (edl?.tracks || []).find((t) => t.kind === "vo");
  const subs = edl?.subtitles || [];
  const groups = [];
  let cur = null;
  clips.forEach((c) => {
    const gk = c.group_key || c.frame_id;
    if (!cur || cur.group_key !== gk) { cur = { group_key: gk, clips: [] }; groups.push(cur); }
    cur.clips.push(c);
  });
  groups.forEach((g) => {
    const startSec = Math.min(...g.clips.map((c) => c.start));
    const endSec = Math.max(...g.clips.map((c) => c.start + c.duration));
    g.startFrame = Math.round(startSec * fps);
    g.durationInFrames = Math.max(1, Math.round((endSec - startSec) * fps));
    const m = g.clips[0].meta || {};
    // shape `lead` like a shot row so existing consumers (inspector, marks) keep working
    g.lead = { frame_id: g.clips[0].frame_id, src: g.clips[0].src, poster: g.clips[0].poster, mediaType: g.clips[0].mediaType,
               speed: g.clips[0].speed, frame_id_first: g.clips[0].frame_id,
               authored_shot_number: m.shot, frame_number: m.shot, label: m.label, timecode: m.timecode, shot_type: m.shot_type };
    g.frames = g.clips.map((c) => ({ frame_id: c.frame_id }));
    g.has_audio = !!voTrack?.clips.some((v) => v.group_key === g.group_key);
    const sub = subs.find((s) => s.group_key === g.group_key)
      || subs.find((s) => Math.abs((s.start || 0) - startSec) < 0.05);
    g.vo_text = sub?.text || null;
    g.vo_speaker = sub?.speaker || null;
  });
  return groups;
}

/** Reflow the EDL so its shot-groups follow `newOrderKeys`: video clips repacked
 *  end-to-end in that order, each group's VO clip + subtitle moved to the group's
 *  new start. Music/SFX clips keep their absolute positions. Returns a new EDL. */
export function applyGroupOrder(edl, newOrderKeys) {
  const videoTrack = (edl?.tracks || []).find((t) => t.kind === "video");
  if (!videoTrack || !videoTrack.clips.length) return edl;

  const byGroup = new Map();
  const seen = [];
  videoTrack.clips.forEach((c) => {
    const gk = c.group_key || c.frame_id;
    if (!byGroup.has(gk)) { byGroup.set(gk, []); seen.push(gk); }
    byGroup.get(gk).push(c);
  });
  // requested order first (only known groups), then any not mentioned, in current order
  const order = [
    ...(newOrderKeys || []).filter((gk) => byGroup.has(gk)),
    ...seen.filter((gk) => !(newOrderKeys || []).includes(gk)),
  ];

  const oldStart = new Map();
  byGroup.forEach((clips, gk) => oldStart.set(gk, Math.min(...clips.map((c) => c.start))));

  let offset = 0;
  const newVideoClips = [];
  const groupNewStart = new Map();
  order.forEach((gk) => {
    const clips = byGroup.get(gk);
    const gStart = oldStart.get(gk);
    const delta = offset - gStart;
    let gEnd = offset;
    clips.forEach((c) => {
      const ns = c.start + delta;
      newVideoClips.push({ ...c, start: ns });
      gEnd = Math.max(gEnd, ns + c.duration);
    });
    groupNewStart.set(gk, offset);
    offset = gEnd;
  });

  const tracks = edl.tracks.map((t) => {
    if (t.kind === "video") return { ...t, clips: newVideoClips };
    if (t.kind === "vo") return { ...t, clips: t.clips.map((c) => groupNewStart.has(c.group_key) ? { ...c, start: groupNewStart.get(c.group_key) } : c) };
    return t; // music / sfx stay put
  });

  const subtitles = (edl.subtitles || []).map((s) => {
    let gk = s.group_key;
    if (!gk) { for (const [k, st] of oldStart) if (Math.abs(st - (s.start || 0)) < 0.05) { gk = k; break; } }
    return gk && groupNewStart.has(gk) ? { ...s, group_key: gk, start: groupNewStart.get(gk) } : s;
  });

  const ends = tracks.flatMap((t) => t.clips.map((c) => c.start + c.duration));
  const totalSec = Math.max(offset, ...(ends.length ? ends : [0]));
  return { ...edl, tracks, subtitles, order, totalSec };
}

/** Insert a dropped visual clip (image/video) into the video track at sequence
 *  index `atIndex` (a new shot between existing ones), then reflow everything. */
export function insertVisualClip(edl, clip, atIndex) {
  const videoTrack = (edl?.tracks || []).find((t) => t.kind === "video");
  if (!videoTrack) return edl;
  const withClip = { ...edl, tracks: edl.tracks.map((t) => t.kind === "video" ? { ...t, clips: [...t.clips, clip] } : t) };
  const base = [];
  videoTrack.clips.forEach((c) => { const gk = c.group_key || c.frame_id; if (!base.includes(gk)) base.push(gk); });
  const idx = Math.max(0, Math.min(atIndex, base.length));
  base.splice(idx, 0, clip.group_key);
  return applyGroupOrder(withClip, base);
}

/** Blade/split: cut the single-clip video shot whose span contains `atSec` into two
 *  independent shots (the second gets a fresh group_key + advanced in-point), then
 *  repack the spine. Multi-keyframe still-groups aren't split (returns edl unchanged). */
export function splitVideoGroupAt(edl, atSec) {
  const vt = (edl?.tracks || []).find((t) => t.kind === "video");
  if (!vt) return edl;
  const byGroup = new Map();
  vt.clips.forEach((c) => { const k = c.group_key || c.frame_id; if (!byGroup.has(k)) byGroup.set(k, []); byGroup.get(k).push(c); });
  let target = null;
  for (const [k, clips] of byGroup) {
    if (clips.length !== 1) continue;                       // only single-clip shots, v1
    const c = clips[0];
    if (atSec > c.start + 0.15 && atSec < c.start + c.duration - 0.15) { target = { k, c }; break; }
  }
  if (!target) return edl;
  const { c } = target;
  const local = atSec - c.start;
  const rnd = () => Math.random().toString(36).slice(2, 7);
  const newKey = `${c.group_key || c.frame_id}~${rnd()}`;
  const c1 = { ...c, duration: local };
  const c2 = { ...c, id: `${c.id}~b${rnd()}`, group_key: newKey, start: c.start + local,
    in: (c.in || 0) + local, duration: c.duration - local };
  const clips = [];
  vt.clips.forEach((x) => { if (x === c) clips.push(c1, c2); else clips.push(x); });
  const order = [];
  clips.forEach((x) => { const g = x.group_key || x.frame_id; if (!order.includes(g)) order.push(g); });
  const withSplit = { ...edl, tracks: edl.tracks.map((t) => t.kind !== "video" ? t : { ...t, clips }) };
  return applyGroupOrder(withSplit, order);
}

/** Index in the shot sequence at a given time (for inserting a dropped asset). */
export function groupIndexAtTime(edl, fps, sec) {
  const groups = groupsFromEdl(edl, fps);
  let i = 0;
  for (const g of groups) {
    const start = g.startFrame / fps;
    const mid = start + (g.durationInFrames / fps) / 2;
    if (sec >= mid) i = groups.indexOf(g) + 1; else break;
  }
  return i;
}
